import React, { useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import HotSwapDashboardSearch from './hot-swap-dashboard-search'
import HotSwapOrdersTable from './hot-swap-orders-table'
import { isEmptyList } from '../../../utils'
import { Loading } from '../../root/components/common'
import { HOTSWAP_TYPES } from '../constants/constants'
import { LOCAL_LOCALE_KEY } from '../../root/constants'
import { ExportFileIcon } from '../../root/components/common/export-icon'
import { enqueueSnackbar } from '../../root/actions'
import { downloadFile } from 'web/src/utils'
import { getHotswapsFileForExport } from '../api/hotswaps-api'
import { composeFileName } from '../../../utils'
import classNames from 'classnames'

export default function HotSwapTabOrder({ filters, onSearch, onSortBy }) {
    const classes = useStyles()

    const [isExporting, setIsExporting] = useState(false)

    const { locale } = useSelector(state => state.main)
    const { adminLocations, isLoading } = useSelector(state => state.hotswap)

    const dispatch = useDispatch()
    const history = useHistory()

    const hotSwaps = useMemo(() => {
        return adminLocations?.map(location => ({
            location: location.name,
            hotswap_type: location.hotswap_type,
            parentLocation: location.parent_location_name,
            databaseId: location.id,
            id: `HS${location.id}`,
            customer: location.customer_name,
            status: location.status,
            responsibleOrTracking:
                location.hotswap_type === HOTSWAP_TYPES.SBU
                    ? location.tracking_number
                    : location.technician_email,
            devices: location.device_count,
            date: location.date,
            time: location.time
        }))
    }, [adminLocations])

    function onRowClick(location) {
        history.push(`/hot-swap/${location.databaseId}`)
    }

    const exportHotswaps = async () => {
        setIsExporting(true)
        const fileType = 'xlsx'
        const locale_key = localStorage.getItem(LOCAL_LOCALE_KEY)
        const params = { fileType, locale_key, ...filters }
        const blob = await getHotswapsFileForExport(params)
        if (blob) {
            const fileName = composeFileName('hotswap_export', fileType)
            downloadFile(blob, fileType, fileName)
        } else {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.getDeviceReportsEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
        setIsExporting(false)
    }

    return (
        <div className={classes.wrapper}>
            {isExporting && (
                <div className={classes.loader}>
                    <Loading />
                </div>
            )}
            <div
                className={classNames(classes.toolbarWrapper, {
                    [`${classes.disabledWrapper}`]: isExporting
                })}>
                <div onClick={() => exportHotswaps('xlsx')}>
                    <ExportFileIcon
                        fileType="excel"
                        size="big"
                        locale={locale}
                    />
                </div>
                <HotSwapDashboardSearch
                    locale={locale}
                    onSearch={onSearch}
                    filters={filters}
                />
            </div>

            {!isEmptyList(hotSwaps) && (
                <div style={{ height: '100%' }}>
                    <HotSwapOrdersTable
                        hotSwaps={hotSwaps}
                        search={filters.search}
                        onRowClick={onRowClick}
                        isLoading={isLoading}
                        sorting={filters.sort_by}
                        onSort={onSortBy}
                    />
                </div>
            )}
        </div>
    )
}

const useStyles = makeStyles(() => ({
    wrapper: {
        height: 'calc(100% - 160px)',
        display: 'flex',
        flexDirection: 'column'
    },
    disabledWrapper: {
        opacity: 0.3,
        pointerEvents: 'none'
    },
    loader: {
        top: '50vh',
        zIndex: 9,
        position: 'absolute',
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    toolbarWrapper: {
        display: 'flex',
        marginBottom: '20px',
        maxWidth: '333px',
        alignSelf: 'flex-end',
        gap: 16,
        alignItems: 'center'
    }
}))
